// Comparativa de la orden de cocina actual (la que se envía) con la orden de
// cocina nueva (motor de la lista de compra), dentro del panel Comparativa de
// la pestaña Lista de compra. Por ingrediente y por plato: actual, nueva,
// diferencia, motivo y cuál es la correcta; y si la orden nueva cuadra con la
// lista de compra (debe cuadrar siempre). Solo lectura.
import React, { useCallback, useEffect, useState } from 'react';
import { ordenCocinaV2API } from '../../api/api';
import { useToast } from '../../hooks/use-toast';
import { TEXTO_ORDEN_NUEVA_PRUEBAS } from '../../utils/tareasPresupuesto';

const claseTh = 'px-3 py-2 text-xs font-bold tracking-wide uppercase text-[#5B6B64] border-b border-[#E1E6E3]';
const claseTd = 'px-3 py-2.5 text-sm border-b border-[#EEF1EF] align-top';
const TEXTO_CORRECTA = { nueva: 'Orden nueva', revisar: 'Revisar', coinciden: 'Coinciden' };

const formatCantidad = (n) => (n === null || n === undefined
  ? '-'
  : (Number(n) || 0).toLocaleString('es-ES', { maximumFractionDigits: 2 }));

const ComparativaOrdenCocinaNueva = ({ presupuestoId, jornadaId }) => {
  const { toast } = useToast();
  const [abierta, setAbierta] = useState(false);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [soloDiferencias, setSoloDiferencias] = useState(true);

  const cargar = useCallback(async () => {
    if (!presupuestoId) return;
    setCargando(true);
    try {
      const { data } = await ordenCocinaV2API.comparativa(presupuestoId, jornadaId);
      setDatos(data);
    } catch (err) {
      setDatos(null);
      toast({
        title: 'No se pudo comparar con la orden de cocina nueva',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setCargando(false);
    }
  }, [presupuestoId, jornadaId, toast]);

  useEffect(() => {
    if (abierta) cargar();
  }, [abierta, cargar]);

  if (!abierta) {
    return (
      <button
        type="button"
        onClick={() => setAbierta(true)}
        className="self-start text-sm font-semibold text-[#11704F] hover:text-[#0B5139] hover:underline"
        data-testid="comparar-orden-cocina-nueva"
      >
        Comparar con la orden de cocina nueva
      </button>
    );
  }

  const resumen = datos?.resumen || {};
  const resumenPlatos = datos?.resumen_platos || {};
  const cuadre = datos?.cuadre_lista_compra || {};
  const filas = (datos?.lineas || []).filter(f => !soloDiferencias || f.cuadra === false);
  const platos = (datos?.platos || []).filter(p => !soloDiferencias || !p.cuadra);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-[#E1E6E3] px-3 py-3" data-testid="comparativa-orden-cocina-nueva">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-bold text-[#16221D]">Orden de cocina actual frente a la nueva</div>
          <div className="text-xs text-[#92400E]">{TEXTO_ORDEN_NUEVA_PRUEBAS}</div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={cargar} disabled={cargando} className="text-xs font-semibold text-[#11704F] hover:underline disabled:opacity-50">
            {cargando ? 'Comparando...' : 'Volver a comparar'}
          </button>
          <button type="button" onClick={() => setAbierta(false)} className="text-xs font-semibold text-[#5B6B64] hover:underline">
            Cerrar
          </button>
        </div>
      </div>

      {cargando && !datos && <p className="text-sm text-[#5B6B64]">Comparando las dos órdenes...</p>}

      {datos && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-[#4A5A53]">
            <div className="rounded-lg bg-[#F7F9F8] px-3 py-2">
              <div className="font-bold text-[#16221D]">Ingredientes</div>
              {resumen.cuadran || 0} cuadran · {resumen.difieren || 0} difieren ({resumen.explicadas || 0} explicadas)
            </div>
            <div className="rounded-lg bg-[#F7F9F8] px-3 py-2">
              <div className="font-bold text-[#16221D]">Platos</div>
              {resumenPlatos.cuadran || 0} cuadran · {resumenPlatos.difieren || 0} difieren
            </div>
            <div className={`rounded-lg px-3 py-2 ${cuadre.cuadra === false ? 'bg-[#FDECEA] text-[#8A1C12]' : 'bg-[#E3F3EC] text-[#0F5E43]'}`} data-testid="comparativa-orden-cuadre">
              <div className="font-bold">Orden nueva y lista de compra</div>
              {cuadre.cuadra === false ? 'No cuadran: revisar' : (cuadre.cuadra ? 'Cuadran' : 'Sin datos')}
            </div>
          </div>
          {datos.nota && <p className="text-xs text-[#5B6B64]">{datos.nota}</p>}
          {datos.error_orden_actual && <p className="text-xs text-[#8A1C12]">Orden actual no disponible: {datos.error_orden_actual}</p>}

          <label className="flex items-center gap-2 text-xs text-[#4A5A53]">
            <input type="checkbox" checked={soloDiferencias} onChange={(e) => setSoloDiferencias(e.target.checked)} />
            Solo las diferencias
          </label>

          {filas.length === 0 ? (
            <p className="text-sm text-[#0F5E43]">Ingredientes: todo cuadra.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse">
                <thead>
                  <tr>
                    <th className={`${claseTh} text-left`}>Ingrediente</th>
                    <th className={`${claseTh} text-right`}>Actual</th>
                    <th className={`${claseTh} text-right`}>Nueva</th>
                    <th className={`${claseTh} text-right`}>Diferencia</th>
                    <th className={`${claseTh} text-left`}>Motivo y cuál es correcta</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map(f => (
                    <tr key={f.ingrediente_id}>
                      <td className={claseTd}>
                        <div className="font-semibold">{f.nombre}</div>
                        <div className="text-xs text-[#5B6B64]">{f.unidad}</div>
                      </td>
                      <td className={`${claseTd} text-right tabular-nums`}>{formatCantidad(f.actual)}</td>
                      <td className={`${claseTd} text-right tabular-nums font-bold`}>{formatCantidad(f.nueva)}</td>
                      <td className={`${claseTd} text-right tabular-nums ${f.cuadra ? 'text-[#0F5E43]' : 'bg-[#FDF0DC] text-[#7A3A06] font-bold'}`}>
                        {formatCantidad(f.diferencia)}
                      </td>
                      <td className={`${claseTd} text-xs`}>
                        {(f.motivos || []).map(m => m.texto).join(' ')}
                        {f.correcta && f.correcta !== 'coinciden' && (
                          <div className={f.correcta === 'nueva' ? 'text-[#0F5E43]' : 'text-[#92400E]'}>
                            Correcta: {TEXTO_CORRECTA[f.correcta] || f.correcta}. {f.por_que}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {platos.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse">
                <thead>
                  <tr>
                    <th className={`${claseTh} text-left`}>Plato</th>
                    <th className={`${claseTh} text-right`}>Raciones actual</th>
                    <th className={`${claseTh} text-right`}>Raciones nueva</th>
                    <th className={`${claseTh} text-left`}>Motivo y cuál es correcta</th>
                  </tr>
                </thead>
                <tbody>
                  {platos.map(p => (
                    <tr key={`${p.jornada_id || 'evento'}|${p.menu || ''}|${p.plato}`}>
                      <td className={claseTd}>
                        <div className="font-semibold">{p.plato}</div>
                        <div className="text-xs text-[#5B6B64]">{p.jornada ? `${p.jornada} · ` : ''}{p.menu}</div>
                      </td>
                      <td className={`${claseTd} text-right tabular-nums`}>{formatCantidad(p.actual)}</td>
                      <td className={`${claseTd} text-right tabular-nums font-bold`}>{formatCantidad(p.nueva)}</td>
                      <td className={`${claseTd} text-xs`}>
                        {p.motivo ? p.motivo.texto : 'Coinciden'}
                        {p.motivo && (
                          <div className={p.correcta === 'nueva' ? 'text-[#0F5E43]' : 'text-[#92400E]'}>
                            Correcta: {TEXTO_CORRECTA[p.correcta] || p.correcta}. {p.motivo.por_que}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ComparativaOrdenCocinaNueva;
